import { useCallback, useState } from 'react';
import { keywordService } from '../services';
import type {
  Keyword,
  KeywordCreate,
  KeywordListParams,
  KeywordListResponse,
  KeywordRankHistoryResponse,
  KeywordUpdate,
} from '../types';

export function useKeywords() {
  const [keywords, setKeywords] = useState<KeywordListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchKeywords = useCallback(
    async (placeId: number, params: KeywordListParams = {}) => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await keywordService.listKeywords(placeId, params);
        setKeywords(data);
        return data;
      } catch (err) {
        const message = err instanceof Error ? err.message : '키워드 목록을 불러오는데 실패했습니다';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const createKeyword = useCallback(
    async (placeId: number, data: KeywordCreate) => {
      setIsLoading(true);
      setError(null);
      try {
        const created = await keywordService.createKeyword(placeId, data);
        return created;
      } catch (err) {
        const message = err instanceof Error ? err.message : '키워드 등록에 실패했습니다';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    keywords,
    isLoading,
    error,
    fetchKeywords,
    createKeyword,
  };
}

export function useKeyword() {
  const [keyword, setKeyword] = useState<Keyword | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchKeyword = useCallback(async (keywordId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await keywordService.getKeyword(keywordId);
      setKeyword(data);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : '키워드를 불러오는데 실패했습니다';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateKeyword = useCallback(async (keywordId: number, data: KeywordUpdate) => {
    setIsLoading(true);
    setError(null);
    try {
      const updated = await keywordService.updateKeyword(keywordId, data);
      setKeyword(updated);
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : '키워드 수정에 실패했습니다';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteKeyword = useCallback(async (keywordId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      await keywordService.deleteKeyword(keywordId);
      setKeyword(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : '키워드 삭제에 실패했습니다';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    keyword,
    isLoading,
    error,
    fetchKeyword,
    updateKeyword,
    deleteKeyword,
  };
}

export function useKeywordRankHistory() {
  const [history, setHistory] = useState<KeywordRankHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(
    async (
      keywordId: number,
      params?: { start_date?: string; end_date?: string; period?: '7d' | '30d' | '90d' }
    ) => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await keywordService.getKeywordRankHistory(keywordId, params);
        setHistory(data);
        return data;
      } catch (err) {
        const message = err instanceof Error ? err.message : '순위 히스토리를 불러오는데 실패했습니다';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    history,
    isLoading,
    error,
    fetchHistory,
  };
}
